import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

type PWAStatus =
  | "not-installed"
  | "installable"
  | "installed"
  | "running-as-pwa";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

interface NavigatorWithRelatedApps extends Navigator {
  getInstalledRelatedApps?: () => Promise<
    Array<{ platform: string; url: string }>
  >;
}

interface UsePWAInstallReturn {
  installPWA: () => Promise<void>;
  isInstallable: boolean;
  isInstalled: boolean;
  isRunningAsPWA: boolean;
  openPWA: () => void;
  pwaStatus: PWAStatus;
}

export const usePWAInstall = (): UsePWAInstallReturn => {
  const [pwaStatus, setPwaStatus] = useState<PWAStatus>("not-installed");
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Check if already running as PWA
    const isRunningAsPWA =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as NavigatorWithStandalone).standalone === true;

    if (isRunningAsPWA) {
      setPwaStatus("running-as-pwa");
      return;
    }

    // Check if PWA is already installed (but not running)
    const checkInstalledApps = async () => {
      const nav = navigator as NavigatorWithRelatedApps;
      if (nav.getInstalledRelatedApps) {
        const relatedApps = await nav.getInstalledRelatedApps();
        if (relatedApps.length > 0) {
          setPwaStatus("installed");
        }
      }
    };

    checkInstalledApps();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setPwaStatus("installable");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setPwaStatus("installed");
      setDeferredPrompt(null);
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const installPWA = async (): Promise<void> => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    await deferredPrompt.userChoice;

    setDeferredPrompt(null);
  };

  const openPWA = (): void => {
    navigate("/");
  };

  return {
    installPWA,
    isInstallable: pwaStatus === "installable",
    isInstalled: pwaStatus === "installed",
    isRunningAsPWA: pwaStatus === "running-as-pwa",
    openPWA,
    pwaStatus,
  };
};
